import { appBaseUrl } from "../../config/apiConfig";
import { TFileUploadRequest, TUploadFileResponse } from "./types";
import { TFileTypeConfig } from "../../../../features/Files/types";
import { getText } from "../assistantsApi/methods";
import { getMimeTypeFromFileExtensions } from "../../../../features/Files/helpers";

export const fileUploadWithFormData = (
  file: File,
  filename: string,
): Promise<TUploadFileResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("filename", filename);

  return appBaseUrl
    .post("save-the-file/file-upload", formData)
    .then((res) => res.data);
};

export const fileUpload = (
  file: TFileUploadRequest,
): Promise<TUploadFileResponse> => {
  return appBaseUrl.post("save-the-file/file", file).then((res) => res.data);
};

export const getFileContentByHash = async (
  hash: string,
  fileConfig: TFileTypeConfig,
  fileName: string,
  signal?: AbortSignal,
): Promise<string | null> => {
  switch (fileConfig.strategy) {
    case "as-text": {
      const response = await appBaseUrl.get(`save-the-file/file/${hash}`, {
        responseType: "arraybuffer",
        signal,
      });
      return new TextDecoder("utf-8").decode(response.data);
    }
    case "blob": {
      const response = await appBaseUrl.get(`save-the-file/file/${hash}`, {
        responseType: "blob",
        signal,
      });
      const blob = new Blob([response.data], { type: fileConfig.mimeType });
      return URL.createObjectURL(blob);
    }
    case "extract-text": {
      const response = await appBaseUrl.get(`save-the-file/file/${hash}`, {
        responseType: "arraybuffer",
        signal,
      });
      const file = new File([response.data], fileName, {
        type: fileConfig.mimeType,
      });
      const text = await getText(file);
      return text;
    }
    case "unsupported": {
      return null;
    }
    default:
      throw new Error(
        `Unsupported file processing strategy: ${fileConfig.strategy}`,
      );
  }
};

export const getFileUrlByHash = async (
  hash: string,
  fileName: string,
  signal?: AbortSignal,
): Promise<{ url: string; fileName: string }> => {
  const response = await appBaseUrl.get(`save-the-file/file/${hash}`, {
    responseType: "blob",
    signal,
  });
  const url = URL.createObjectURL(response.data);
  return { url, fileName };
};

export const getFileByHash = async (
  hash: string,
  fileName: string,
  signal?: AbortSignal,
): Promise<File> => {
  const response = await appBaseUrl.get(`save-the-file/file/${hash}`, {
    responseType: "arraybuffer",
    signal,
  });

  const extension = fileName.split(".").pop() ?? "";
  const mimeType = getMimeTypeFromFileExtensions(extension);

  return new File([response.data], fileName, { type: mimeType });
};

export const getFileTextByHash = async (
  hash: string,
  fileName: string,
  signal?: AbortSignal,
): Promise<string> => {
  const response = await appBaseUrl.get(`save-the-file/file/${hash}`, {
    responseType: "arraybuffer",
    signal,
  });
  const extension = fileName.split(".").pop() ?? "";
  const mimeType = getMimeTypeFromFileExtensions(extension);
  const file = new File([response.data], fileName, {
    type: mimeType,
  });
  const text = await getText(file);
  return text;
};
